import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
	type CodeFixEdits,
	type ImageAttachment
} from '@/api-types';
import { getFileType } from '@/utils/string';
import { logger } from '@/utils/logger';
import { appEvents } from '@/lib/app-events';
import { useCodeGenStore } from '@/stores/codeGenStore';
// client-side AI is handled in the store; no direct import needed here
import { createUserMessage, type ChatMessage } from '../utils/message-helpers';
import { initialStages as defaultStages, updateStage as updateStageHelper } from '../utils/project-stage-helpers';
import type { ProjectStage } from '../utils/project-stage-helpers';


export interface FileType {
	filePath: string;
	fileContents: string;
	explanation?: string;
	isGenerating?: boolean;
	needsFixing?: boolean;
	hasErrors?: boolean;
	language?: string;
}

// New interface for phase timeline tracking
export interface PhaseTimelineItem {
	id: string;
	name: string;
	description: string;
	files: {
		path: string;
		purpose: string;
		status: 'generating' | 'completed' | 'error' | 'validating';
		contents?: string;
	}[];
	status: 'generating' | 'completed' | 'error' | 'validating';
	timestamp: number;
}

export function useChat({
	chatId: urlChatId,
	query: userQuery,
	images: _userImages,
	agentMode = 'deterministic',
	onDebugMessage: _onDebugMessage,
	onTerminalMessage: _onTerminalMessage,
}: {
	chatId?: string;
	query: string | null;
	images?: ImageAttachment[];
	agentMode?: 'deterministic' | 'smart';
	onDebugMessage?: (type: 'error' | 'warning' | 'info' | 'websocket', message: string, details?: string, source?: string, messageType?: string, rawMessage?: unknown) => void;
	onTerminalMessage?: (log: { id: string; content: string; type: 'command' | 'stdout' | 'stderr' | 'info' | 'error' | 'warn' | 'debug'; timestamp: number; source?: string }) => void;
}) {
	const renderIdRef = useRef(Math.random().toString(36).substr(2, 9));
	const renderCountRef = useRef(0);
	renderCountRef.current++;

	console.log('[useChat] Hook called, renderID:', renderIdRef.current, 'renderCount:', renderCountRef.current, 'urlChatId:', urlChatId, 'userQuery:', userQuery);

	// Use code generation store for state management
	const codeGenStore = useCodeGenStore();

	// Local state for UI-specific concerns
	const [chatId, setChatId] = useState<string>();
	const [messages, setMessages] = useState<ChatMessage[]>([
		{ type: 'ai', id: 'main', message: 'Thinking...', isThinking: true },
	]);
	const [projectStages, setProjectStages] = useState<ProjectStage[]>(defaultStages);
	const [edit, setEdit] = useState<Omit<CodeFixEdits, 'type'>>();
	const [isInitialStateRestored, setIsInitialStateRestored] = useState(false);
	void isInitialStateRestored; // mark as used to satisfy TS noUnusedLocals
		// One-time init guard to prevent duplicate session initialization
		const hasInitializedRef = useRef(false);
		console.log('[useChat] hasInitializedRef.current:', hasInitializedRef.current, 'renderID:', renderIdRef.current);


	const updateStage = useCallback(
		(stageId: ProjectStage['id'], data: Partial<Omit<ProjectStage, 'id'>>) => {
			logger.debug('updateStage', { stageId, ...data });
			setProjectStages(prev => updateStageHelper(prev, stageId, data));
		},
		[],
	);

	const onCompleteBootstrap = useCallback(() => {
		updateStage('bootstrap', { status: 'completed' });
	}, [updateStage]);

	const clearEdit = useCallback(() => {
		setEdit(undefined);
	}, []);

	const sendMessage = useCallback((message: Omit<ChatMessage, 'type'>) => {
		// Add conversational messages to the chat UI
		setMessages(prev => [...prev, { ...message, type: 'ai' as const }]);
	}, []);

	const sendUserMessage = useCallback((message: string) => {
		setMessages(prev => [...prev, createUserMessage(message)]);
	}, []);

	const loadBootstrapFiles = useCallback((files: FileType[]) => {
		const filesWithLanguage = files.map((file) => ({
			...file,
			language: getFileType(file.filePath),
		}));
		codeGenStore.addBootstrapFiles(filesWithLanguage);
	}, [codeGenStore]);
	void loadBootstrapFiles; // mark as used to satisfy TS noUnusedLocals
	void agentMode; // mark as used

	// Generate blueprint using client-side AI (now preceded by Research)
	const generateBlueprint = useCallback(async (query: string) => {
		console.log('[generateBlueprint] ENTRY - query:', query, 'agentId:', codeGenStore.agentId);

		try {
			console.log('[generateBlueprint] Setting stages and UI messages');
			// 1) Research stage
			updateStage('research', { status: 'active' });
			sendMessage({ id: 'research', message: 'Researching prior art, frameworks, and trade-offs...', isThinking: true });
			await codeGenStore.runResearch(query);
			updateStage('research', { status: 'completed' });

			// 2) Planning stage
			updateStage('planning', { status: 'active' });
			sendMessage({ id: 'planning', message: 'Creating comprehensive technical specifications and architecture...', isThinking: true });
			await codeGenStore.runPlanning(query);
			updateStage('planning', { status: 'completed' });

			// 3) Architecture stage
			updateStage('architecture', { status: 'active' });
			sendMessage({ id: 'architecture', message: 'Creating detailed task breakdown and implementation strategy...', isThinking: true });
			await codeGenStore.runArchitecture(query);
			updateStage('architecture', { status: 'completed' });

			// 4) Bootstrap and Blueprint stages
			updateStage('bootstrap', { status: 'completed' });
			updateStage('blueprint', { status: 'active' });

			sendMessage({
				id: 'main',
				message: 'Blueprint is being generated...',
				isThinking: true,
			});

			console.log('[generateBlueprint] Calling codeGenStore.generateBlueprint');
			await codeGenStore.generateBlueprint(query);
			console.log('[generateBlueprint] ✅ codeGenStore.generateBlueprint completed');

			updateStage('blueprint', { status: 'completed' });
			sendMessage({
				id: 'main',
				message: 'Blueprint generation complete. Now starting the code generation...',
				isThinking: true,
			});

			console.log('[generateBlueprint] ✅ Blueprint generation completed successfully');
		} catch (error) {
			console.error('[generateBlueprint] ❌ Blueprint generation FAILED:', error);
			logger.error('Blueprint generation failed:', error);
			sendMessage({
				id: 'blueprint_error',
				message: `❌ Blueprint generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
			});

			const errMsg = error instanceof Error ? error.message : String(error);
			if (errMsg.includes('No AI provider API key configured')) {
				// Provide actionable guidance
				sendMessage({
					id: 'blueprint_next_steps',
					message:
						'➡️ Fix: Add an API key in Settings → API Keys & Secrets. In development, you can also set NANOGPT_API_KEY (primary) and/or GEMINI_API_KEY (Google AI Studio), then reload.',
				});
				toast.error('Missing AI API key. Go to Settings → API Keys & Secrets to configure.');
			}
			throw error;
		}
	}, [codeGenStore, updateStage, sendMessage]);

	// Start code generation process
	const startCodeGeneration = useCallback(async () => {
		console.log('[startCodeGeneration] ENTRY - agentId:', codeGenStore.agentId);

		try {
			console.log('[startCodeGeneration] Calling codeGenStore.startGeneration');
			await codeGenStore.startGeneration();
			console.log('[startCodeGeneration] ✅ codeGenStore.startGeneration completed');

			sendMessage({
				id: 'generation_started',
				message: '🚀 Code generation started...',
				isThinking: true,
			});

			console.log('[startCodeGeneration] ✅ Code generation started successfully');
		} catch (error) {
			console.error('[startCodeGeneration] ❌ Code generation start FAILED:', error);
			logger.error('Code generation failed:', error);
			sendMessage({
				id: 'generation_error',
				message: `❌ Code generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
			});
			throw error;
		}
	}, [codeGenStore, sendMessage]);

	// Initialize session and handle chat logic
	const initializeChat = useCallback(async () => {
		console.log('[initializeChat] ENTRY - hasInitializedRef:', hasInitializedRef.current, 'urlChatId:', urlChatId, 'renderID:', renderIdRef.current, 'timestamp:', Date.now());

		if (hasInitializedRef.current) {
			console.log('[initializeChat] BLOCKED by guard, renderID:', renderIdRef.current);
			return;
		}

		hasInitializedRef.current = true;
		console.log('[initializeChat] Guard set to TRUE, proceeding with initialization, renderID:', renderIdRef.current);
		if (!urlChatId) {
			console.log('[initializeChat] Early return: urlChatId is falsy, renderID:', renderIdRef.current);
			return;
		}

		try {
			console.log('[initializeChat] Branch decision - urlChatId:', urlChatId);
			if (urlChatId === 'new') {
				if (!userQuery) {
					const errorMsg = 'Please enter a description of what you want to build';
					logger.error('Query is required for new code generation');
					toast.error(errorMsg);
					return;
				}

				// Generate unique agent ID for new session
				const agentId = `agent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
				console.log('[initializeChat] Generated agentId:', agentId, 'renderID:', renderIdRef.current);
				// IMPORTANT: Defer route/nav changes until after generation starts to avoid re-render race

				sendMessage({
					id: 'main',
					message: "Sure, let's get started. Bootstrapping the project first...",
					isThinking: true,
				});

				// Initialize new session in store
				await codeGenStore.initializeSession(agentId, userQuery);
				console.log('[initializeChat] ✅ initializeSession completed, renderID:', renderIdRef.current);
				console.log('[initializeChat] 🔍 CHECKPOINT 1 - About to proceed to generateBlueprint');
				console.log('[initializeChat] 🔍 Current state - agentId:', agentId, 'userQuery:', userQuery);
				console.log('[initializeChat] 🔄 About to call generateBlueprint with query:', userQuery);

				try {
					await generateBlueprint(userQuery);
					console.log('[initializeChat] ✅ generateBlueprint completed successfully');
				} catch (error) {
					console.error('[initializeChat] ❌ generateBlueprint FAILED:', error);
					throw error;
				}

				console.log('[initializeChat] 🔍 CHECKPOINT 2 - About to call startCodeGeneration');
				console.log('[initializeChat] 🔍 State before start - agentId:', agentId, 'projectId:', codeGenStore.projectId);
				console.log('[initializeChat] 🔄 About to call startCodeGeneration');
				try {
					await startCodeGeneration();
					console.log('[initializeChat] ✅ startCodeGeneration completed successfully');
					// Now that generation has started, update chatId to trigger navigation
					setChatId(agentId);
				} catch (error) {
					console.error('[initializeChat] ❌ startCodeGeneration FAILED:', error);
					throw error;
				}

				// Emit app-created event for sidebar updates
				appEvents.emitAppCreated(agentId, {
					title: userQuery || 'New App',
					description: userQuery,
				});

			} else {
				console.log('[initializeChat] ENTER ELSE branch - loading existing session for:', urlChatId);
				// Load existing session
				sendMessage({
					id: 'fetching-chat',
					message: 'Fetching your previous chat...',
					isThinking: false,
				});

				try {
					await codeGenStore.loadExistingSession(urlChatId);
					setChatId(urlChatId);

					sendMessage({
						id: 'resuming-chat',
						message: 'Starting from where you left off...',
						isThinking: false,
					});

					setIsInitialStateRestored(true);
				} catch (error) {
					logger.error('Failed to load existing chat:', error);
					sendMessage({
						id: 'load-error',
						message: `❌ Failed to load chat: ${error instanceof Error ? error.message : 'Unknown error'}`,
					});
				}
			}

			console.log('[DEBUG] initializeChat: Initialization completed successfully');
		} catch (error) {
			logger.error('Error initializing chat:', error);
			sendMessage({
				id: 'init-error',
				message: `❌ Failed to initialize chat: ${error instanceof Error ? error.message : 'Unknown error'}`,
			});
		}
	}, [urlChatId, userQuery, generateBlueprint, startCodeGeneration, sendMessage, codeGenStore]);


	// Initialize chat on mount

		// Track unmounts to diagnose lifecycle issues
		useEffect(() => {
			const idAtMount = renderIdRef.current;
			return () => {
				console.log('[useChat] UNMOUNT - renderID:', idAtMount, 'timestamp:', Date.now());
			};
		}, []);

	useEffect(() => {
		console.log('[useEffect] initializeChat effect triggered, renderID:', renderIdRef.current, 'timestamp:', Date.now());
		initializeChat();
	}, [initializeChat]);

	// Track callback recreation
	useEffect(() => {
		console.log('[useEffect] initializeChat callback reference CHANGED, renderID:', renderIdRef.current);
	}, [initializeChat]);

	// Handle file edits
	useEffect(() => {
		if (edit) {
			// When edit is cleared, write the edit changes
			return () => {
				codeGenStore.updateFile(edit.filePath, {
					fileContents: codeGenStore.files.find(f => f.filePath === edit.filePath)?.fileContents?.replace(
						edit.search,
						edit.replacement,
					) || ''
				});
			};
		}
	}, [edit, codeGenStore]);

	// Control functions for deployment and generation
	const handleStopGeneration = useCallback(() => {
		codeGenStore.stopGeneration();
		sendMessage({
			id: 'generation_stopped',
			message: '⏸️ Code generation stopped.',
		});
	}, [codeGenStore, sendMessage]);

	const handleResumeGeneration = useCallback(async () => {
		await codeGenStore.resumeGeneration();
		sendMessage({
			id: 'generation_resumed',
			message: '▶️ Code generation resumed...',
			isThinking: true,
		});
	}, [codeGenStore, sendMessage]);

	const handleDeployToCloudflare = useCallback(async (instanceId: string) => { logger.debug('Deploy requested for', instanceId);
		try {
			sendMessage({
				id: 'deployment_started',
				message: '🚀 Starting deployment to Cloudflare...',
				isThinking: true,
			});

			await codeGenStore.deployToCloudflare();

			sendMessage({
				id: 'deployment_success',
				message: `✅ Deployment successful! Your app is now live at: ${codeGenStore.cloudflareDeploymentUrl}`,
			});
		} catch (error) {
			logger.error('❌ Deployment failed:', error);
			sendMessage({
				id: 'deployment_error',
				message: `❌ Deployment failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\n🔄 You can try again.`,
			});
		}
	}, [codeGenStore, sendMessage]);

	return {
		messages,
		edit,
		bootstrapFiles: codeGenStore.bootstrapFiles,
		chatId,
		query: codeGenStore.query,
		files: codeGenStore.files,
		blueprint: codeGenStore.blueprint,
		blueprintMarkdown: codeGenStore.blueprintMarkdown,
		previewUrl: codeGenStore.previewUrl,
		isGeneratingBlueprint: codeGenStore.isGeneratingBlueprint,
		isBootstrapping: codeGenStore.isBootstrapping,
		totalFiles: codeGenStore.totalFiles,
		websocket: (null as unknown as import('partysocket').WebSocket | null), // No longer using WebSocket
		sendUserMessage,
		sendAiMessage: sendMessage,
		clearEdit,
		projectStages,
		phaseTimeline: codeGenStore.phaseTimeline,
		isThinking: codeGenStore.isThinking,
		onCompleteBootstrap,
		// Deployment and generation control
		isDeploying: codeGenStore.isDeploying,
		cloudflareDeploymentUrl: codeGenStore.cloudflareDeploymentUrl,
		deploymentError: codeGenStore.deploymentError,
		isRedeployReady: codeGenStore.isRedeployReady,
		isGenerationPaused: codeGenStore.isGenerationPaused,
		isGenerating: codeGenStore.isGenerating,
		handleStopGeneration,
		handleResumeGeneration,
		handleDeployToCloudflare,
		// Preview refresh control
		shouldRefreshPreview: codeGenStore.shouldRefreshPreview,
		// Preview deployment state
		isPreviewDeploying: codeGenStore.isPreviewDeploying,
		// Phase progress visual indicator
		isPhaseProgressActive: codeGenStore.isPhaseProgressActive,
	};
}
